import { useCallback, useRef } from 'react';

/**
 * A registry that lets you map **logical field names** to DOM refs.
 *
 * `fieldName` is a logical field identifier (e.g. 'name', 'description', COLUMN_IDS.SHARES),
 * not the React prop name. It should match whatever you use in PROPERTIES_TO_FIELDS_MAP.
 *
 * Usage:
 *   const { registerFieldRef, getFieldRef, registerWrapperRef } = useFieldRefRegistry();
 *   <input ref={registerFieldRef('name')} />
 *   <div ref={registerWrapperRef('currency', wrapperRef)}>...</div>
 */

export const useFieldRefRegistry = () => {
  // Internal map: fieldName -> HTMLElement | null
  const registryRef = useRef({});

  /**
   * Returns a ref callback you can pass directly to `ref` prop.
   * `fieldName` is the logical field id (e.g. 'name', 'description', COLUMN_IDS.SHARES).
   * Example: <input ref={registerFieldRef('name')} />
   */

  const registerFieldRef = useCallback(
    fieldName => element => {
      if (element) {
        registryRef.current[fieldName] = element;
      } else {
        delete registryRef.current[fieldName];
      }
    },
    [],
  );

  /**
   * Registers a wrapper element and finds the actual input using a ref.
   * The ref should point to the actual input element inside the wrapper.
   * Usage:
   *   const inputRef = useRef(null);
   *   <div ref={registerWrapperRef('currency', inputRef)}>
   *     <BitaSelect ref={inputRef} ... />
   *   </div>
   */
  const registerWrapperRef = useCallback(
    (fieldName, inputRef) => wrapperElement => {
      if (wrapperElement && inputRef?.current) {
        // Register the actual input element from the ref
        registryRef.current[fieldName] = inputRef.current;
      } else {
        delete registryRef.current[fieldName];
      }
    },
    [],
  );

  /**
   * Returns the current element for a given fieldName, or null.
   */
  const getFieldRef = useCallback(fieldName => {
    return registryRef.current[fieldName] || null;
  }, []);

  return {
    registerFieldRef,
    registerWrapperRef,
    getFieldRef,
  };
};
